import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { DEAL_STAGES, SUBSCRIPTION_PLANS, formatSGD } from "@/lib/dealStages";
import { usePipelineOptions } from "@/lib/pipelineOptions";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { BarChart3, RefreshCcw, TrendingUp, Trophy, Timer, Percent, Target } from "lucide-react";

export default function AdminReports() {
  const { admin } = useAuth();
  const { getStageMeta, getPlanMeta } = usePipelineOptions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports/summary");
      setData(data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (admin?.role === "admin") fetchData(); }, [fetchData, admin]);

  if (admin && admin.role !== "admin") return <Navigate to="/admin" replace />;

  const kpis = data?.kpis || {};
  const funnelMax = Math.max(1, ...(data?.funnel || []).map((f) => f.count));
  const sourceMax = Math.max(1, ...(data?.by_source || []).map((s) => s.n));

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-reports-page">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">Insights</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-reports-heading">
              Sales Reports
            </h1>
            <p className="mt-2 text-sm text-brand-slate/75">
              Pipeline health, win rate, cycle time, and rep performance — all in one view.
            </p>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-white rounded-full transition-colors"
            data-testid="reports-refresh"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>

        {loading && <p className="text-sm text-brand-slate/60">Loading report…</p>}

        {!loading && data && (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Kpi icon={TrendingUp} label="Open Pipeline" value={formatSGD(kpis.open_value_sgd)} sub={`${kpis.open_count || 0} deals`} accent testid="kpi-open" />
              <Kpi icon={Trophy} label="Won Contract Value" value={formatSGD(kpis.won_value_sgd)} sub={`${kpis.won_count || 0} deals`} testid="kpi-won" />
              <Kpi icon={Percent} label="Win Rate" value={`${kpis.win_rate ?? 0}%`} sub="of closed deals" testid="kpi-win-rate" />
              <Kpi icon={Timer} label="Avg Cycle" value={`${kpis.avg_cycle_days ?? 0} days`} sub="lead → won" testid="kpi-cycle" />
            </div>

            {/* This Month */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <Kpi label="Won This Month" value={formatSGD(kpis.won_this_month_value_sgd)} sub={`${kpis.won_this_month_count || 0} deals`} testid="kpi-won-month" />
              <Kpi label="Total Deals" value={kpis.total_deals ?? 0} testid="kpi-total" />
              <Kpi label="Reps" value={(data.leaderboard || []).length} testid="kpi-reps" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
              {/* Pipeline by stage */}
              <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="report-pipeline-card">
                <div className="flex items-center gap-2 mb-5">
                  <BarChart3 size={16} className="text-brand-orange" />
                  <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Pipeline by stage</h2>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-brand-cream/60">
                      <TableHead className="text-brand-navy">Stage</TableHead>
                      <TableHead className="text-brand-navy text-right">Count</TableHead>
                      <TableHead className="text-brand-navy text-right">Value</TableHead>
                      <TableHead className="text-brand-navy text-right hidden sm:table-cell">Avg</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_stage || []).map((row) => {
                      const meta = getStageMeta(row.stage);
                      return (
                        <TableRow key={row.stage} data-testid={`report-stage-${row.stage}`}>
                          <TableCell>
                            <Badge className={`${meta.color} rounded-full`}>{meta.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right text-brand-navy">{row.count}</TableCell>
                          <TableCell className="text-right text-brand-navy font-semibold">{formatSGD(row.value_sgd)}</TableCell>
                          <TableCell className="text-right text-brand-slate hidden sm:table-cell">{row.count ? formatSGD(row.avg_value_sgd) : "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </section>

              {/* Conversion Funnel */}
              <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="report-funnel-card">
                <div className="flex items-center gap-2 mb-5">
                  <Target size={16} className="text-brand-orange" />
                  <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Conversion funnel</h2>
                </div>
                <ul className="space-y-2">
                  {(data.funnel || []).map((row) => {
                    const pct = Math.round((row.count / funnelMax) * 100);
                    return (
                      <li key={row.label} className="flex items-center gap-3" data-testid={`funnel-row-${row.label.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                        <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-brand-navy/70">{row.label}</span>
                        <div className="flex-1 h-3 bg-brand-cream rounded-full overflow-hidden">
                          <div className="h-full bg-brand-orange transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-10 text-right text-xs text-brand-slate/70">{row.count}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>

            {/* Rep leaderboard */}
            <section className="bg-white border border-brand-navy/10 rounded-2xl p-6 mb-10" data-testid="report-leaderboard-card">
              <div className="flex items-center gap-2 mb-5">
                <Trophy size={16} className="text-brand-orange" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Rep leaderboard</h2>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-brand-cream/60">
                      <TableHead className="text-brand-navy">Rep</TableHead>
                      <TableHead className="text-brand-navy text-right">Open</TableHead>
                      <TableHead className="text-brand-navy text-right">Open Value</TableHead>
                      <TableHead className="text-brand-navy text-right">Won</TableHead>
                      <TableHead className="text-brand-navy text-right">Won Value</TableHead>
                      <TableHead className="text-brand-navy text-right hidden md:table-cell">Win Rate</TableHead>
                      <TableHead className="text-brand-navy text-right hidden md:table-cell">Activities</TableHead>
                      <TableHead className="text-brand-navy text-right hidden md:table-cell">Tasks Done</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.leaderboard || []).length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-10 text-brand-slate/60">No reps found.</TableCell></TableRow>
                    )}
                    {(data.leaderboard || []).map((r) => (
                      <TableRow key={r.owner_id} data-testid={`leaderboard-row-${r.owner_id}`}>
                        <TableCell className="font-medium text-brand-navy">{r.name}</TableCell>
                        <TableCell className="text-right text-brand-slate">{r.open_count}</TableCell>
                        <TableCell className="text-right text-brand-navy">{formatSGD(r.open_value_sgd)}</TableCell>
                        <TableCell className="text-right text-brand-slate">{r.won_count}</TableCell>
                        <TableCell className="text-right text-brand-navy font-semibold">{formatSGD(r.won_value_sgd)}</TableCell>
                        <TableCell className="text-right text-brand-slate hidden md:table-cell">{r.win_rate}%</TableCell>
                        <TableCell className="text-right text-brand-slate hidden md:table-cell">{r.activities}</TableCell>
                        <TableCell className="text-right text-brand-slate hidden md:table-cell">
                          {r.tasks_done} <span className="text-brand-slate/50">/ +{r.tasks_open} open</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* By plan */}
              <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="report-plan-card">
                <div className="flex items-center gap-2 mb-5">
                  <BarChart3 size={16} className="text-brand-orange" />
                  <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">By subscription plan</h2>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-brand-cream/60">
                      <TableHead className="text-brand-navy">Plan</TableHead>
                      <TableHead className="text-brand-navy text-right">Deals</TableHead>
                      <TableHead className="text-brand-navy text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_plan || []).map((row) => {
                      const meta = getPlanMeta(row.plan);
                      return (
                        <TableRow key={row.plan} data-testid={`report-plan-${row.plan}`}>
                          <TableCell className="font-medium text-brand-navy">{meta?.label || row.plan}</TableCell>
                          <TableCell className="text-right text-brand-slate">{row.count}</TableCell>
                          <TableCell className="text-right text-brand-navy font-semibold">{formatSGD(row.value_sgd)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </section>

              {/* Lead source */}
              <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="report-source-card">
                <div className="flex items-center gap-2 mb-5">
                  <BarChart3 size={16} className="text-brand-orange" />
                  <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Where leads come from</h2>
                </div>
                <ul className="space-y-2">
                  {(data.by_source || []).length === 0 && (
                    <li className="text-sm text-brand-slate/60 py-4 text-center">No leads yet.</li>
                  )}
                  {(data.by_source || []).map((row) => {
                    const pct = Math.round((row.n / sourceMax) * 100);
                    return (
                      <li key={row.source} className="flex items-center gap-3" data-testid={`report-source-row-${row.source}`}>
                        <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-brand-navy/70">{row.source}</span>
                        <div className="flex-1 h-2 bg-brand-cream rounded-full overflow-hidden">
                          <div className="h-full bg-brand-orange" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-8 text-right text-xs text-brand-slate/70">{row.n}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>

            <p className="mt-8 text-[11px] text-brand-slate/50 text-right">
              Generated {formatDateTime(data.generated_at)}
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div
      className={`p-5 border bg-white rounded-2xl ${accent ? "border-brand-orange/40" : "border-brand-navy/10"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className={accent ? "text-brand-orange" : "text-brand-slate/60"} />}
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-slate/65">{label}</p>
      </div>
      <p className={`mt-2 text-2xl font-semibold ${accent ? "text-brand-orange" : "text-brand-navy"}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] text-brand-slate/60">{sub}</p>}
    </div>
  );
}
